import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Nullable } from "../../../pojo/Util";
import { Table } from "../../s25-table/Table";
import { Bind } from "../../../decorators/bind.decorator";
import { S25EditableNumberComponent } from "../../s25-editable/s25-editable-number/s25.editable.number.component";
import { S25Util } from "../../../util/s25-util";
import { EventProfileMicroService } from "../../../services/event.profile.micro.service";
import { S25EventDetailsInlineEditService } from "./s25.event.details.inline.edit.service";

@TypeManagerDecorator("s25-ng-editable-headcount")
@Component({
    selector: "s25-ng-editable-headcount",
    template: `
        @if (tableConfig()) {
            <s25-ng-table [dataSource]="tableConfig()" [pivotThreshold]="300"></s25-ng-table>
        } @else if (counts[0]) {
            @let count = counts[0];
            <div>
                <label>Expected</label>
                <s25-ng-editable-number
                    [val]="count.expected"
                    [hasCommit]="true"
                    [readOnly]="!canEdit"
                    (valChange)="updateCount(count.profileId, 'expected', $event)"
                ></s25-ng-editable-number>
            </div>
            <div>
                <label>Registered</label>
                <s25-ng-editable-number
                    [val]="count.registered"
                    [hasCommit]="true"
                    [readOnly]="!canEdit"
                    (valChange)="updateCount(count.profileId, 'registered', $event)"
                ></s25-ng-editable-number>
            </div>
        }
    `,
    host: {
        "[class.single-profile]": "counts.length === 1",
        "[class.edit-mode]": "canEdit",
    },
    styles: `
        :host {
            &.single-profile {
                display: flex;
                flex-wrap: wrap;
                gap: 2em;

                & > div {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
            }

            &.edit-mode {
                & > div {
                    align-items: flex-start;

                    label {
                        position: relative;
                        top: 7px;
                    }
                }
            }

            label {
                font-weight: 400 !important;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class EvEditableHeadcountComponent implements OnInit {
    @Input() itemId: number;
    @Input() counts: HeadCount[] = [];
    @Input() canEdit: boolean;

    tableConfig = signal<Nullable<Table.DataSource>>(null);

    private readonly inlineEditService = inject(S25EventDetailsInlineEditService);

    ngOnInit() {
        this.counts ??= [];
        if (this.counts.length > 1) {
            this.tableConfig.set({
                type: "unpaginated",
                dataSource: () => Promise.resolve({ rows: this.counts.map(this.mapToRows) }),
                columns: [
                    { id: "name", header: "Profile" },
                    { id: "expected", header: "Expected" },
                    { id: "registered", header: "Registered" },
                ],
            });
        }
    }

    async updateCount(profileId: number, prop: "expected" | "registered", value: number) {
        const [_, err] = await S25Util.Maybe(
            this.inlineEditService.runInlineEditScenarios(this.itemId, () =>
                EventProfileMicroService.updateProfileMinimal(this.itemId, profileId, { [`${prop}Count`]: value }),
            ),
        );
        if (err) console.error(err);
    }

    @Bind
    mapToRows(count: HeadCount, index: number) {
        return {
            id: index++,
            name: count.name,
            cells: {
                name: {
                    text: count.name,
                },
                expected: {
                    component: S25EditableNumberComponent,
                    inputs: {
                        val: count.expected,
                        hasCommit: true,
                        readOnly: !this.canEdit,
                    },
                    outputs: {
                        valChange: (val: number) => this.updateCount(count.profileId, "expected", val),
                    },
                },
                registered: {
                    component: S25EditableNumberComponent,
                    inputs: {
                        val: count.registered,
                        hasCommit: true,
                        readOnly: !this.canEdit,
                    },
                    outputs: {
                        valChange: (val: number) => this.updateCount(count.profileId, "registered", val),
                    },
                },
            },
        };
    }
}

export type HeadCount = {
    name: string;
    profileId: number;
    expected: number;
    registered: number;
};
